import React, { useState, useEffect, useMemo } from 'react';
import confetti from 'canvas-confetti';
import {
  Clock,
  Flag,
  CheckCircle2,
  XCircle,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Award,
  FileText,
  BrainCircuit,
  HelpCircle,
  Strikethrough,
  MapPin,
  Send,
  AlertCircle,
  AlertTriangle,
  Zap,
  BookOpen,
  Lock,
  Timer,
  FastForward,
  Info,
  Loader2,
} from 'lucide-react';
import {
  UserProfile,
  PraxisPaper,
  TestAttempt,
  Question,
  EssayPrompt,
  ExamMode,
  PreviewUsage,
  QuestionMastery,
} from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { STATE_STANDARDS } from '../data/stateStandards';
import {
  getPaperMetadata,
  resolvePaperDocId,
  getQuestionsForPaperFromFirestore,
  getEssayPromptsForPaperFromFirestore,
  recordQuestionAttempt,
  isPaperUnlockedForUser,
  isUserAdmin,
  getOrCreateAnonymousSessionId,
  getPreviewAuthHeaders,
  ensureGuestPreviewClaimed,
  savePersonalFlashcardToFirestore,
  getUserQuestionMastery,
} from '../lib/firebase';
import {
  DifficultyTier,
  calculateNextDifficulty,
  getWeakestInSessionDomain,
  selectNextAdaptiveQuestion,
} from '../lib/adaptiveSessionEngine';

interface TestSimulatorProps {
  paperId: string;
  userProfile: UserProfile;
  onFinishTest: (attempt: TestAttempt) => void;
  onBackToPapers: () => void;
  mode?: ExamMode | 'exam' | 'practice';
  customQuestions?: Question[];
  customTitle?: string;
  openPricingModal?: () => void;
  // Guests who finish their free questions are sent to sign up / log in.
  onRequestSignup?: (mode: 'signup' | 'signin') => void;
}

// Helper to extract crisp 1-sentence rationale for Rapid Fire mode
export const getOneSentenceRationale = (q: Question): string => {
  if (q.shortExplanation && q.shortExplanation.trim()) {
    return q.shortExplanation.trim();
  }
  if (!q.explanation)
    return 'The correct answer aligns with educator competency standards.';
  const clean = q.explanation.replace(/[*_#`]/g, '').trim();
  const sentenceMatch = clean.match(/^([^.!?]+[.!?])/);
  if (sentenceMatch && sentenceMatch[1] && sentenceMatch[1].length > 15) {
    return sentenceMatch[1].trim();
  }
  return clean.length > 130 ? clean.slice(0, 127) + '...' : clean;
};

export const TestSimulator: React.FC<TestSimulatorProps> = ({
  paperId,
  userProfile,
  onFinishTest,
  onBackToPapers,
  mode = 'timed',
  customQuestions,
  customTitle,
  openPricingModal,
  onRequestSignup,
}) => {
  // Normalize mode
  const effectiveMode: ExamMode =
    mode === 'practice' || mode === 'study'
      ? 'study'
      : mode === 'rapid'
        ? 'rapid'
        : mode === 'adaptive'
          ? 'adaptive'
          : 'timed';

  const [examAttemptId] = useState<string>(
    () =>
      'attempt_' +
      Date.now() +
      '_' +
      Math.random().toString(36).substring(2, 7),
  );
  const defaultPaper =
    PRAXIS_PAPERS.find((p) => p.id === paperId) || PRAXIS_PAPERS[0];
  const [paper, setPaper] = useState<PraxisPaper>(() => {
    if (customTitle) {
      return {
        ...defaultPaper,
        title: customTitle,
        totalQuestions: customQuestions?.length || defaultPaper.totalQuestions,
      };
    }
    return defaultPaper;
  });
  const [questions, setQuestions] = useState<Question[]>(
    customQuestions || defaultPaper.questions || [],
  );
  const [loadingContent, setLoadingContent] =
    useState<boolean>(!customQuestions);

  // In-session Adaptive Difficulty State (strictly within session; doesn't overwrite SM-2)
  const [adaptiveQuestions, setAdaptiveQuestions] = useState<Question[]>([]);
  const [adaptiveDifficulty, setAdaptiveDifficulty] =
    useState<DifficultyTier>('medium');
  const [adaptiveStreak, setAdaptiveStreak] = useState<number>(0);
  const [adaptiveUsedIds, setAdaptiveUsedIds] = useState<Set<string>>(
    new Set(),
  );
  const [inSessionDomainStats, setInSessionDomainStats] = useState<
    Record<string, { correct: number; total: number }>
  >({});

  // Hoisted above the data-loading effect below (previously computed further
  // down) because the loader now needs to branch on tier: free-tier/anonymous
  // callers can no longer read praxisPapers/{paperId}/questions directly
  // (see firestore.rules), so their question set has to come from the new
  // server-side preview endpoint instead.
  const isAdmin = isUserAdmin(userProfile);
  const isSubscribed = userProfile.tier !== 'free';
  const isFreeTier = !isAdmin && !isSubscribed;
  // Guests get a real per-browser session id instead of the shared
  // 'guest_user_1' placeholder DEFAULT_PROFILE uses -- otherwise every
  // anonymous visitor site-wide would draw down the same preview quota.
  const isGuest = !userProfile?.uid || userProfile.uid === 'guest_user_1';
  const previewIdentifier = isGuest
    ? getOrCreateAnonymousSessionId()
    : userProfile.uid;
  // What the server says this caller may answer on this paper (guests: 5 in
  // total across the site; free accounts: 5 per paper, plus 5 more on the
  // paper they tried as a guest).
  const [previewAllowance, setPreviewAllowance] = useState<{
    kind: 'guest' | 'account';
    limit: number;
    used: number;
    usedElsewhere: number;
  } | null>(null);

  useEffect(() => {
    if (customQuestions && customQuestions.length > 0) {
      setQuestions(customQuestions);
      if (customTitle) {
        setPaper((prev) => ({
          ...prev,
          title: customTitle,
          totalQuestions: customQuestions.length,
        }));
      }
      setLoadingContent(false);
      return;
    }

    // 2026-10-08 speed round: this used to call getPraxisPapersFromFirestore()
    // first -- which downloads EVERY question of EVERY paper (~16,000 docs,
    // one paper after another) just to read this paper's title and timing --
    // and then loaded the questions and essay prompts one after the other.
    // Now it reads only this paper's own document and its own question pool,
    // and fetches the paper details, questions and essay prompts in parallel.
    async function loadSimulatorData() {
      try {
        const resolvedId = await resolvePaperDocId(paperId);

        const metaTask = getPaperMetadata(resolvedId).then((meta) => {
          if (meta) setPaper(meta);
        });

        const questionsTask = (async () => {
          if (isFreeTier) {
            // Subscription-gated papers can no longer be read directly by a
            // free-tier/anonymous browser, so the capped free-preview set is
            // fetched from the server (which holds an admin-authenticated
            // Firestore session) instead of getQuestionsForPaperFromFirestore().
            // Show the loader while (re)loading, so a guest who just signed
            // up never sees the old "used up" screen flash.
            setLoadingContent(true);
            try {
              // A guest who just signed up: move their free questions onto
              // the account first, so the 5 bonus questions are in place.
              if (!isGuest) await ensureGuestPreviewClaimed();
              const authHeaders = isGuest ? {} : await getPreviewAuthHeaders();
              const previewRes = await fetch('/api/preview/questions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders },
                body: JSON.stringify({
                  paperId: resolvedId || paperId,
                  identifier: previewIdentifier,
                }),
              });
              const previewData = await previewRes.json();
              if (previewRes.ok) {
                setQuestions(previewData.questions || []);
                if (previewData.usage) {
                  setPreviewUsage(previewData.usage);
                }
                if (previewData.allowance) {
                  setPreviewAllowance(previewData.allowance);
                }
              } else {
                console.warn(
                  'TestSimulator: preview endpoint returned an error:',
                  previewData?.error,
                );
              }
            } catch (previewErr) {
              console.warn(
                'TestSimulator: Error loading free-preview questions:',
                previewErr,
              );
            }
          } else {
            const firestoreQs = await getQuestionsForPaperFromFirestore(resolvedId);
            if (firestoreQs && firestoreQs.length > 0) {
              setQuestions(firestoreQs);
            }
          }
        })();

        const promptsTask = getEssayPromptsForPaperFromFirestore(resolvedId).then((firestorePrompts) => {
          if (firestorePrompts && firestorePrompts.length > 0) {
            setEssayPromptsList(firestorePrompts);
          }
        });

        await Promise.all([metaTask, questionsTask, promptsTask]);
      } catch (err) {
        console.warn(
          'TestSimulator: Error loading questions/prompts from Firestore:',
          err,
        );
      } finally {
        setLoadingContent(false);
      }
    }
    loadSimulatorData();
  }, [paperId, customQuestions, customTitle, isFreeTier, previewIdentifier]);

  const activeState =
    STATE_STANDARDS.find((s) => s.code === userProfile.targetState) ||
    STATE_STANDARDS[0];
  const passScoreTarget =
    activeState.passingScores[paper.code] ??
    (paper.passingScoreEts !== null && paper.passingScoreEts !== undefined
      ? paper.passingScoreEts
      : 150);

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, number>>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<
    Record<string, boolean>
  >({});
  const [struckOptions, setStruckOptions] = useState<Record<string, boolean>>(
    {},
  ); // "qId-optIdx"

  const [previewUsage, setPreviewUsage] = useState<PreviewUsage | null>(null);

  // Cross-mode "already seen" tracking (roadmap item #2, 2026-09-07): the
  // app has recorded per-question mastery in users/{uid}/questionMastery
  // since QuestionAttempt.mode was added, but nothing ever read it back
  // into question SELECTION -- study/rapid/timed pools were pure random
  // shuffles of the whole bank every time, so a student could see the same
  // question five sessions in a row. QuestionAttempt.mode covers
  // exam/practice/study/timed/rapid/adaptive, so one mastery map here
  // gives real "seen before, in any mode" behavior with zero new writes.
  const [questionMastery, setQuestionMastery] = useState<
    Record<string, QuestionMastery>
  >({});
  useEffect(() => {
    if (!userProfile?.uid || userProfile.uid === 'guest_user_1') {
      setQuestionMastery({});
      return;
    }
    let cancelled = false;
    getUserQuestionMastery(userProfile.uid)
      .then((records) => {
        if (cancelled) return;
        const map: Record<string, QuestionMastery> = {};
        for (const rec of records) {
          if (rec && rec.questionId) map[rec.questionId] = rec;
        }
        setQuestionMastery(map);
      })
      .catch((err) => {
        console.warn('TestSimulator: could not load question mastery:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [userProfile?.uid]);

  // Reorders a shuffled pool so genuinely-unseen questions come first
  // (in their already-randomized order), followed by previously-seen
  // questions ordered least-recently-seen-first -- i.e. repeats only ever
  // surface once the unseen pool for that paper is exhausted, and even
  // then the "stalest" repeat comes back before a very recent one.
  const prioritizeUnseen = (pool: Question[]): Question[] => {
    if (Object.keys(questionMastery).length === 0) return pool;
    const unseen: Question[] = [];
    const seen: Question[] = [];
    for (const q of pool) {
      if (questionMastery[q.id]) seen.push(q);
      else unseen.push(q);
    }
    seen.sort((a, b) => {
      const aTime = new Date(
        questionMastery[a.id]?.lastAttemptedAt || 0,
      ).getTime();
      const bTime = new Date(
        questionMastery[b.id]?.lastAttemptedAt || 0,
      ).getTime();
      return aTime - bTime;
    });
    return [...unseen, ...seen];
  };

  // Timers
  const [timeLeftSeconds, setTimeLeftSeconds] = useState<number>(
    (paper.durationMinutes && paper.durationMinutes > 0
      ? paper.durationMinutes
      : 90) * 60,
  );
  const [rapidTimeLeft, setRapidTimeLeft] = useState<number>(45);
  const [rapidFlashActive, setRapidFlashActive] = useState<boolean>(false);
  const [rapidFlashSeconds, setRapidFlashSeconds] = useState<number>(3);

  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<{
    score: number;
    percentage: number;
    passed: boolean;
    correctCount: number;
    totalQuestions: number;
  } | null>(null);

  // Helper to extract initial essay prompts with fallback for legacy essayPrompt and constructedResponsePrompts
  const getNormalizedPrompts = (p: PraxisPaper): EssayPrompt[] => {
    if (p.essayPrompts && p.essayPrompts.length > 0) return p.essayPrompts;
    if (
      p.constructedResponsePrompts &&
      p.constructedResponsePrompts.length > 0
    ) {
      return p.constructedResponsePrompts.map((cr, idx) => ({
        id: cr.id || `cr_${idx}`,
        paperId: cr.paperId || p.id,
        type: 'argumentative' as const,
        title: `Constructed Response: ${cr.domain || `Task ${idx + 1}`}`,
        promptText: cr.promptText,
        timeLimitMinutes: cr.timeLimitMinutes || 10,
        rubricNotes: cr.rubricNotes,
      }));
    }
    if (p.essayPrompt && p.essayPrompt.promptText) {
      return [
        {
          id: p.essayPrompt.id || 'ep1',
          paperId: p.id,
          type: (p.essayPrompt.type || 'argumentative') as
            | 'argumentative'
            | 'informative-explanatory',
          title: p.essayPrompt.title || 'Writing Task',
          promptText: p.essayPrompt.promptText,
          timeLimitMinutes: p.essayPrompt.timeLimitMinutes || 30,
          sample6Response: p.essayPrompt.sample6Response,
          rubricNotes: p.essayPrompt.rubricNotes,
        },
      ];
    }
    return [];
  };

  // Essay state (if essay prompt exists)
  const [essayPromptsList, setEssayPromptsList] = useState<EssayPrompt[]>(() =>
    getNormalizedPrompts(defaultPaper),
  );
  const [selectedPromptIndex, setSelectedPromptIndex] = useState<number>(0);
  const [essayText, setEssayText] = useState<string>('');
  const [essayFeedback, setEssayFeedback] = useState<string | null>(null);
  const [evaluatingEssay, setEvaluatingEssay] = useState<boolean>(false);

  const activeEssayPrompts =
    essayPromptsList.length > 0
      ? essayPromptsList
      : getNormalizedPrompts(paper);
  const currentEssayPrompt =
    activeEssayPrompts[selectedPromptIndex] || activeEssayPrompts[0] || null;

  // AI Tutor Modal state
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [loadingAiExplanation, setLoadingAiExplanation] =
    useState<boolean>(false);
  const [showAiModal, setShowAiModal] = useState<boolean>(false);

  // Initialize first question for Adaptive Mode
  useEffect(() => {
    if (
      effectiveMode === 'adaptive' &&
      adaptiveQuestions.length === 0 &&
      (questions.length > 0 || (paper.questions && paper.questions.length > 0))
    ) {
      const pool = questions.length > 0 ? questions : paper.questions || [];
      const initialUsed = new Set<string>();
      const firstQ = selectNextAdaptiveQuestion(
        pool,
        initialUsed,
        'medium',
        null,
      );
      if (firstQ) {
        initialUsed.add(firstQ.id);
        setAdaptiveQuestions([firstQ]);
        setAdaptiveUsedIds(initialUsed);
        setAdaptiveDifficulty('medium');
        setAdaptiveStreak(0);
        setInSessionDomainStats({});
      }
    }
  }, [effectiveMode, questions, paper.questions, adaptiveQuestions.length]);

  // Fisher-Yates shuffle algorithm for intentional test randomization
  const shuffleArray = <T,>(array: T[]): T[] => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Exam session question pool resolution:
  // - If custom session (customQuestions), respect user's explicit selection.
  // - If Adaptive Mode: Dynamically sequenced in real time without repeats.
  // - If Study Mode: Draws from full question pool for that paper.
  // - If Rapid Fire Mode: Draws from full question pool for high-speed drill.
  // - If Timed Mode: Takes a genuine Fisher-Yates random sample of paper.questionCount from the total available pool.
  const activeQuestions = useMemo(() => {
    const rawPool = questions.length > 0 ? questions : paper.questions || [];
    if (customQuestions && customQuestions.length > 0) {
      return rawPool;
    }
    if (isFreeTier) {
      // Free preview: keep the server's order (questions already answered
      // first), so someone who signs up mid-way carries on where they were.
      return rawPool;
    }
    if (effectiveMode === 'adaptive') {
      return adaptiveQuestions.length > 0
        ? adaptiveQuestions
        : rawPool.slice(0, 1);
    }
    if (effectiveMode === 'study') {
      return prioritizeUnseen(shuffleArray(rawPool));
    }
    if (effectiveMode === 'rapid') {
      return prioritizeUnseen(shuffleArray(rawPool));
    }
    // Timed Mock Exam mode: sample from the front of the unseen-first
    // ordering, so a capped-length timed set draws new questions before
    // ever repeating one this student has already answered.
    const targetCount =
      paper.questionCount || defaultPaper.totalQuestions || 56;
    if (rawPool.length <= targetCount) {
      return prioritizeUnseen(shuffleArray(rawPool));
    }
    return prioritizeUnseen(shuffleArray(rawPool)).slice(0, targetCount);
  }, [
    questions,
    paper.questions,
    paper.questionCount,
    customQuestions,
    effectiveMode,
    defaultPaper.totalQuestions,
    examAttemptId,
    adaptiveQuestions,
    questionMastery,
    isFreeTier,
  ]);

  const hasMoreAdaptiveQuestions = useMemo(() => {
    if (effectiveMode !== 'adaptive') return false;
    const rawPool = questions.length > 0 ? questions : paper.questions || [];
    return rawPool.some((q) => !adaptiveUsedIds.has(q.id));
  }, [effectiveMode, questions, paper.questions, adaptiveUsedIds]);

  const currentQuestion: Question | null =
    activeQuestions[currentIndex] || activeQuestions[0] || null;

  // Submit Test Calculation
  const handleSubmitTest = () => {
    setIsSubmitted(true);

    let correctCount = 0;
    const domainBreakdown: Record<string, { correct: number; total: number }> =
      {};

    activeQuestions.forEach((q) => {
      if (!domainBreakdown[q.domain]) {
        domainBreakdown[q.domain] = { correct: 0, total: 0 };
      }
      domainBreakdown[q.domain].total += 1;

      if (userAnswers[q.id] === q.correctAnswerIndex) {
        correctCount += 1;
        domainBreakdown[q.domain].correct += 1;
      }
    });

    const percentage =
      activeQuestions.length > 0
        ? Math.round((correctCount / activeQuestions.length) * 100)
        : 0;
    // ETS Scaled score estimation (100 to 200 scale)
    const etsScore = Math.min(
      200,
      Math.max(100, Math.round(100 + percentage * 1.0)),
    );
    const passed = etsScore >= passScoreTarget;

    setTestResult({
      score: etsScore,
      percentage,
      passed,
      correctCount,
      totalQuestions: activeQuestions.length,
    });

    if (passed) {
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
      });
    }

    const attempt: TestAttempt = {
      id: examAttemptId,
      userId: userProfile.uid,
      paperId: paper.id,
      paperCode: paper.code,
      paperTitle: paper.title,
      score: etsScore,
      percentage,
      passed,
      timeSpentSeconds:
        (paper.durationMinutes && paper.durationMinutes > 0
          ? paper.durationMinutes
          : 90) *
          60 -
        timeLeftSeconds,
      domainBreakdown,
      userAnswers,
      completedAt: new Date().toISOString(),
      mode: effectiveMode,
    };

    onFinishTest(attempt);
  };

  const advanceRapidQuestion = () => {
    setRapidFlashActive(false);
    if (currentIndex < activeQuestions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      handleSubmitTest();
    }
  };

  const handleRapidTimeOut = () => {
    // When 45s expires in Rapid Fire:
    // If no option selected, record as unanswered (-1)
    if (currentQuestion && userAnswers[currentQuestion.id] === undefined) {
      setUserAnswers((prev) => ({
        ...prev,
        [currentQuestion.id]: -1,
      }));
    }
    setRapidFlashActive(true);
    setRapidFlashSeconds(3);
  };

  // Reset Rapid Fire timer when currentIndex changes
  useEffect(() => {
    if (effectiveMode === 'rapid') {
      setRapidTimeLeft(45);
      setRapidFlashActive(false);
      setRapidFlashSeconds(3);
    }
  }, [currentIndex, effectiveMode]);

  // Timed Mode Session Countdown Effect
  useEffect(() => {
    if (effectiveMode !== 'timed' || isSubmitted || isPaused) return;

    const interval = setInterval(() => {
      setTimeLeftSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSubmitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [effectiveMode, isSubmitted, isPaused]);

  // Rapid Fire 45-second Per-Question Countdown Effect
  useEffect(() => {
    if (
      effectiveMode !== 'rapid' ||
      isSubmitted ||
      isPaused ||
      rapidFlashActive
    )
      return;

    const interval = setInterval(() => {
      setRapidTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleRapidTimeOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [effectiveMode, isSubmitted, isPaused, rapidFlashActive, currentIndex]);

  // Rapid Fire Flash countdown timer (3s before auto-advancing)
  useEffect(() => {
    if (!rapidFlashActive || isSubmitted) return;

    const interval = setInterval(() => {
      setRapidFlashSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          advanceRapidQuestion();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [rapidFlashActive, currentIndex, activeQuestions.length, isSubmitted]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleSelectOption = (optIdx: number) => {
    if (isSubmitted) return;

    // Timed mode strict locking: once answered, cannot change
    if (
      effectiveMode === 'timed' &&
      userAnswers[currentQuestion.id] !== undefined
    ) {
      return;
    }

    // Rapid Fire locking: once clicked, cannot change
    if (
      effectiveMode === 'rapid' &&
      userAnswers[currentQuestion.id] !== undefined
    ) {
      return;
    }

    setUserAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: optIdx,
    }));

    const isCorrect = optIdx === currentQuestion.correctAnswerIndex;

    // In-session adaptive adjustment (only affects current sitting; separate from SM-2)
    if (effectiveMode === 'adaptive') {
      const dom = currentQuestion.domain || 'General';
      setInSessionDomainStats((prev) => ({
        ...prev,
        [dom]: {
          correct: (prev[dom]?.correct || 0) + (isCorrect ? 1 : 0),
          total: (prev[dom]?.total || 0) + 1,
        },
      }));

      const { nextTier, nextStreak } = calculateNextDifficulty(
        adaptiveDifficulty,
        adaptiveStreak,
        isCorrect,
      );
      setAdaptiveDifficulty(nextTier);
      setAdaptiveStreak(nextStreak);
    }

    if (userProfile?.uid && !isGuest) {
      recordQuestionAttempt(userProfile.uid, {
        questionId: currentQuestion.id,
        paperId: currentQuestion.paperId || paper.id,
        domain: currentQuestion.domain || 'General',
        wasCorrect: isCorrect,
        selectedAnswerIndex: optIdx,
        correctAnswerIndex: currentQuestion.correctAnswerIndex,
        timestamp: new Date().toISOString(),
        mode: effectiveMode,
        examAttemptId,
      }).catch((err) =>
        console.warn('Failed to record question attempt:', err),
      );

      // Auto-generate a personal spaced-repetition flashcard from this
      // mistake so the student can drill the exact concept they just
      // missed. Fire-and-forget: never blocks the answer-selection UI.
      if (!isCorrect) {
        (async () => {
          try {
            const genRes = await fetch(
              '/api/flashcards/generate-from-mistake',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  paperCode: paper.code,
                  paperTitle: paper.title,
                  examCode: paper.examCode,
                  domain: currentQuestion.domain || 'General',
                  questionText: currentQuestion.questionText,
                  options: currentQuestion.options,
                  correctAnswerIndex: currentQuestion.correctAnswerIndex,
                  explanation: currentQuestion.explanation,
                }),
              },
            );
            const genData = await genRes.json();
            if (genRes.ok && genData.flashcard) {
              await savePersonalFlashcardToFirestore(userProfile.uid, {
                paperId: currentQuestion.paperId || paper.id,
                domain:
                  genData.flashcard.domain ||
                  currentQuestion.domain ||
                  'General',
                term: genData.flashcard.term,
                definition: genData.flashcard.definition,
                exampleScenario: genData.flashcard.exampleScenario || '',
                sourceQuestionId: currentQuestion.id,
                scope: 'personal',
                generatedBy: 'auto-from-mistake',
              });
            }
          } catch (err) {
            console.warn(
              'Failed to auto-generate personal flashcard from mistake:',
              err,
            );
          }
        })();
      }

      if (isFreeTier) {
        (isGuest ? Promise.resolve({}) : getPreviewAuthHeaders())
          .then((authHeaders) =>
            fetch('/api/preview/record-attempt', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders },
              body: JSON.stringify({
                paperId: paper.id,
                identifier: previewIdentifier,
                questionId: currentQuestion.id,
              }),
            }),
          )
          .then((r) => r.json())
          .then((u) => {
            if (u && Array.isArray(u.attemptedQuestionIds)) setPreviewUsage(u);
          })
          .catch((err) =>
            console.warn('Failed to record preview question attempt:', err),
          );
      }
    }

    // If Rapid Fire mode, trigger the brief snapshot rationale flash and auto-advance
    if (effectiveMode === 'rapid') {
      setRapidFlashActive(true);
      setRapidFlashSeconds(3);
    }
  };

  const handleAdvanceAdaptive = () => {
    if (currentIndex < activeQuestions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    const rawPool = questions.length > 0 ? questions : paper.questions || [];
    const weakestDomain = getWeakestInSessionDomain(inSessionDomainStats);
    const nextQ = selectNextAdaptiveQuestion(
      rawPool,
      adaptiveUsedIds,
      adaptiveDifficulty,
      weakestDomain,
    );

    if (nextQ) {
      setAdaptiveQuestions((prev) => [...prev, nextQ]);
      setAdaptiveUsedIds((prev) => new Set([...prev, nextQ.id]));
      setCurrentIndex((prev) => prev + 1);
    } else {
      // Question pool for this paper has been completely served in this sitting
      handleSubmitTest();
    }
  };

  const toggleFlag = (qId: string) => {
    setFlaggedQuestions((prev) => ({ ...prev, [qId]: !prev[qId] }));
  };

  const toggleStrikeOption = (qId: string, optIdx: number) => {
    const key = `${qId}-${optIdx}`;
    setStruckOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Helper to render clean explanation text without raw markdown symbols
  const renderCleanExplanation = (rawText: string) => {
    if (!rawText) return null;

    // Strip markdown headers (###, ##, #) and horizontal rules (---, ***)
    const cleaned = rawText
      .replace(/^[#]+\s*/gm, '')
      .replace(/^[-*]{3,}$/gm, '')
      .replace(/\r\n/g, '\n');

    const paragraphs = cleaned.split(/\n\s*\n/).filter((p) => p.trim());

    return (
      <div className="space-y-3">
        {paragraphs.map((p, idx) => {
          const parts = p.split(/(\*\*.*?\*\*)/g);
          return (
            <p key={idx} className="leading-relaxed">
              {parts.map((part, pIdx) => {
                if (
                  part.startsWith('**') &&
                  part.endsWith('**') &&
                  part.length > 4
                ) {
                  return (
                    <strong key={pIdx} className="font-bold text-slate-900">
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                const cleanPart = part.replace(/[*_]/g, '');
                return <span key={pIdx}>{cleanPart}</span>;
              })}
            </p>
          );
        })}
      </div>
    );
  };

  // AI Step-by-Step Question Explanation Call (Streaming)
  const handleAskAiTutor = async () => {
    setShowAiModal(true);
    setLoadingAiExplanation(true);
    setAiExplanation('');
    try {
      const selectedOptText =
        userAnswers[currentQuestion.id] !== undefined
          ? currentQuestion.options[userAnswers[currentQuestion.id]]
          : 'None selected';
      const correctOptText =
        currentQuestion.options[currentQuestion.correctAnswerIndex];

      const res = await fetch('/api/ai/explain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          questionText: currentQuestion.questionText,
          selectedAnswer: selectedOptText,
          correctAnswer: correctOptText,
          options: currentQuestion.options,
          paperName: paper.title,
          stream: true,
        }),
      });

      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({}));
        setAiExplanation(
          `⚠️ AI Guidance Error (${res.status}): ${data.error || 'Server failed to return AI explanation.'}`,
        );
        setLoadingAiExplanation(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const dataStr = trimmed.slice(6);
          if (dataStr === '[DONE]') continue;

          try {
            const parsed = JSON.parse(dataStr);
            if (parsed.error) {
              setAiExplanation(`⚠️ AI Guidance Error: ${parsed.error}`);
              setLoadingAiExplanation(false);
              return;
            }
            if (parsed.text) {
              accumulatedText += parsed.text;
              setAiExplanation(accumulatedText);
            }
          } catch (e) {
            // Ignore parse errors on individual stream chunks
          }
        }
      }

      if (!accumulatedText) {
        setAiExplanation('No explanation content returned.');
      }
    } catch (err: any) {
      console.error('[TestSimulator AI Tutor Network Error]:', err);
      setAiExplanation(
        `⚠️ Connection Error: Unable to communicate with AI service. (${err?.message || 'Network error'})`,
      );
    } finally {
      setLoadingAiExplanation(false);
    }
  };

  // AI Essay Evaluator Call
  const handleEvaluateEssay = async () => {
    if (!essayText.trim() || !currentEssayPrompt) return;
    setEvaluatingEssay(true);
    setEssayFeedback('');
    try {
      const res = await fetch('/api/ai/evaluate-essay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promptText: currentEssayPrompt.promptText,
          essayText: essayText,
          paperName: paper.title,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        console.error(
          '[TestSimulator Essay Evaluator Error]:',
          res.status,
          data.error || data,
        );
        setEssayFeedback(
          `⚠️ Evaluation Error: ${data.error || 'Failed to evaluate essay.'}`,
        );
      } else {
        setEssayFeedback(data.feedback || 'Evaluation completed.');
      }
    } catch (err: any) {
      console.error('[TestSimulator Essay Evaluator Network Error]:', err);
      setEssayFeedback(
        `⚠️ Connection Error: Unable to evaluate essay. (${err?.message || 'Network error'})`,
      );
    } finally {
      setEvaluatingEssay(false);
    }
  };

  // Unanswered Questions Modal State
  const [showUnansweredModal, setShowUnansweredModal] =
    useState<boolean>(false);
  const [unansweredIndices, setUnansweredIndices] = useState<number[]>([]);

  const handleInitiateSubmit = (bypassValidation = false) => {
    if (!bypassValidation) {
      const unanswered: number[] = [];
      activeQuestions.forEach((q, idx) => {
        if (userAnswers[q.id] === undefined || userAnswers[q.id] === null) {
          unanswered.push(idx);
        }
      });

      if (unanswered.length > 0) {
        setUnansweredIndices(unanswered);
        setShowUnansweredModal(true);
        return;
      }
    }

    setShowUnansweredModal(false);
    handleSubmitTest();
  };

  const handleReviewUnanswered = () => {
    setShowUnansweredModal(false);
    if (unansweredIndices.length > 0) {
      setCurrentIndex(unansweredIndices[0]);
    }
  };

  const handleConfirmSubmitAnyway = () => {
    setShowUnansweredModal(false);
    handleSubmitTest();
  };

  const isContentAvailable =
    activeQuestions.length > 0 ||
    activeEssayPrompts.length > 0 ||
    !!currentEssayPrompt;

  // Loading State
  if (loadingContent) {
    return (
      <div className="py-20 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto text-center space-y-4">
        <div className="inline-flex p-4 rounded-2xl bg-indigo-50 text-indigo-600 mb-2 animate-pulse">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">
          Loading Exam Simulator...
        </h2>
        <p className="text-sm text-slate-500">
          Preparing questions and competencies for {paper.title}.
        </p>
      </div>
    );
  }

  // Graceful Empty State for Content in Development (Prevents 0/0 test submissions)
  if (!isContentAvailable) {
    return (
      <div className="py-12 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto space-y-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 sm:p-10 text-center space-y-6">
          <div className="inline-flex p-4 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200">
            <Clock className="w-10 h-10" />
          </div>

          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 uppercase tracking-wide">
              Content In Development
            </div>
            <h2 className="text-2xl font-black text-slate-900">
              Exam Content in Preparation
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
              Standardized practice questions and constructed response prompts
              for{' '}
              <strong className="text-slate-900">
                {paper.title} ({paper.code})
              </strong>{' '}
              are currently being authored according to state certification
              competencies. Full mock exams, adaptive drills, and study modes
              will be available shortly.
            </p>
          </div>

          {paper.domains && paper.domains.length > 0 && (
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 text-left space-y-3">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                Competencies & Target Blueprint:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {paper.domains.map((dom, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between text-xs bg-white p-3 rounded-xl border border-slate-200 shadow-xs"
                  >
                    <span className="font-semibold text-slate-800 truncate pr-2">
                      {dom.name}
                    </span>
                    <span className="text-slate-500 shrink-0 font-mono font-medium">
                      {dom.mcCount ? `${dom.mcCount} MC` : ''}
                      {dom.mcCount && dom.crCount ? ' · ' : ''}
                      {dom.crCount ? `${dom.crCount} CR` : ''}
                      {!dom.mcCount && !dom.crCount
                        ? `${dom.weightPercentage}%`
                        : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={onBackToPapers}
              className="w-full sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
              Return to Catalog
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Free preview gate (2026-10-08). The server only ever serves what is
  // left of the caller's allowance, so running past the served set means
  // the free questions are used up.
  //   - Guests: "Want to keep going?" then the sign up popup. Signing up
  //     gives 5 more questions on this paper.
  //   - Free accounts: the subscription screen.
  if (
    isFreeTier &&
    !customQuestions &&
    currentIndex >= activeQuestions.length
  ) {
    const answeredHere = activeQuestions.filter((q) => userAnswers[q.id] !== undefined);
    const correctHere = answeredHere.filter((q) => userAnswers[q.id] === q.correctAnswerIndex).length;
    const usedOnAnotherPaper = activeQuestions.length === 0 && (previewAllowance?.usedElsewhere || 0) > 0;
    const paperLabel = paper.code ? `${paper.code}` : 'this paper';

    if (isGuest) {
      return (
        <div className="py-12 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-slate-200 bg-white">
            <div className="bg-gradient-to-br from-[#0f1b4d] to-[#1b2f7a] px-6 sm:px-8 py-7 text-white">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-black tracking-[0.2em] text-teal-300">
                <Sparkles className="w-3.5 h-3.5" /> {usedOnAnotherPaper ? 'FREE QUESTIONS USED' : 'NICE WORK'}
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2 leading-tight">
                {usedOnAnotherPaper ? 'Want to try this paper too?' : 'Want to keep going?'}
              </h2>
              {!usedOnAnotherPaper && answeredHere.length > 0 && (
                <p className="text-indigo-100/90 text-sm sm:text-base mt-2">
                  You got <span className="font-extrabold text-white">{correctHere} of {answeredHere.length}</span> right on {paperLabel}.
                </p>
              )}
              {usedOnAnotherPaper && (
                <p className="text-indigo-100/90 text-sm sm:text-base mt-2">
                  You have used your 5 free guest questions on another paper.
                </p>
              )}
            </div>
            <div className="px-6 sm:px-8 py-6 space-y-5">
              <ul className="space-y-2.5 text-sm text-slate-700">
                <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#0E9594] shrink-0 mt-0.5" /> {usedOnAnotherPaper ? `5 free questions on ${paperLabel} and on every other paper` : `5 more free questions on ${paperLabel}, picking up right where you stopped`}</li>
                {!usedOnAnotherPaper && <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#0E9594] shrink-0 mt-0.5" /> 5 free questions on every other paper</li>}
                <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#0E9594] shrink-0 mt-0.5" /> A study plan built around your exam date</li>
                <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#0E9594] shrink-0 mt-0.5" /> Your progress saved, with no card needed</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => (onRequestSignup ? onRequestSignup('signup') : openPricingModal?.())}
                  className="flex-1 bg-pink-500 hover:bg-pink-600 text-white font-black text-sm py-3.5 rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer"
                >
                  Create my free account <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => (onRequestSignup ? onRequestSignup('signin') : openPricingModal?.())}
                  className="sm:w-auto px-5 bg-white border border-slate-300 text-[#0f1b4d] hover:bg-slate-50 font-bold text-sm py-3.5 rounded-2xl cursor-pointer"
                >
                  I have an account
                </button>
              </div>
              <button
                type="button"
                onClick={onBackToPapers}
                className="w-full text-xs font-bold text-slate-500 hover:text-slate-800 cursor-pointer"
              >
                Not now, back to the exams
              </button>
            </div>
          </div>
        </div>
      );
    }

    const servedTotal = Math.max(activeQuestions.length, previewAllowance?.limit || 0);
    return (
      <div className="py-12 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto space-y-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 sm:p-10 text-center space-y-6">
          <div className="inline-flex p-4 rounded-2xl bg-teal-50 text-[#0E9594] border border-teal-200">
            <Sparkles className="w-10 h-10 text-[#0E9594]" />
          </div>

          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-[#0f1b4d] uppercase tracking-wide">
              Free questions used{servedTotal > 0 ? ` (${servedTotal} of ${servedTotal})` : ''}
            </div>
            <h2 className="text-2xl font-black text-[#0f1b4d]">
              Unlock every question on {paperLabel}
            </h2>
            {answeredHere.length > 0 && (
              <p className="text-sm font-bold text-slate-700">
                You got {correctHere} of {answeredHere.length} right.
              </p>
            )}
            <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
              You have used your free practice questions for{' '}
              <strong className="text-slate-900">
                {paper.title} ({paper.code})
              </strong>
              . All Access ($35/mo) unlocks every question, full timed mock
              exams, study guides, flashcards and spaced review on every paper.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            {openPricingModal && (
              <button
                onClick={openPricingModal}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-pink-500 hover:bg-pink-600 text-white font-extrabold text-sm transition shadow-sm flex items-center justify-center gap-2 cursor-pointer"
              >
                <Sparkles className="w-4 h-4 text-amber-200" /> Go All Access ($35/mo)
              </button>
            )}
            <button
              onClick={onBackToPapers}
              className="w-full sm:w-auto px-5 py-3.5 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-700 font-bold text-sm transition cursor-pointer"
            >
              Back to the exams
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-6">
      {/* Top Header Row with Timer & Navigation */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBackToPapers}
            className="p-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded text-xs font-bold bg-indigo-100 text-indigo-800">
                {paper.examCode &&
                !paper.examCode.toLowerCase().startsWith('praxis')
                  ? paper.code.toLowerCase().startsWith('nystce-') ||
                    paper.code.toLowerCase().startsWith('cset-') ||
                    paper.code.toLowerCase().startsWith('cbest-') ||
                    paper.code.toLowerCase().startsWith('ilts-')
                    ? paper.code
                    : `${paper.examCode} ${paper.code}`
                  : `PRAXIS ${paper.code}`}
              </span>
              <span className="text-xs font-bold text-slate-500">
                Question {currentIndex + 1}
                {effectiveMode === 'adaptive'
                  ? ' (Adaptive)'
                  : ` of ${activeQuestions.length}`}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-[11px] font-extrabold uppercase tracking-wide ${
                  effectiveMode === 'study'
                    ? 'bg-emerald-100 text-emerald-800'
                    : effectiveMode === 'rapid'
                      ? 'bg-amber-100 text-amber-800'
                      : effectiveMode === 'adaptive'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-indigo-100 text-indigo-800'
                }`}
              >
                {effectiveMode === 'study'
                  ? 'Study Mode'
                  : effectiveMode === 'rapid'
                    ? 'Rapid Fire'
                    : effectiveMode === 'adaptive'
                      ? 'Adaptive Mode'
                      : 'Timed Exam'}
              </span>
            </div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 line-clamp-1">
              {paper.title}
            </h2>
          </div>
        </div>

        {/* Timer & Controls */}
        <div className="flex items-center gap-4">
          {effectiveMode === 'study' ? (
            <div className="flex items-center gap-2 bg-emerald-600 text-white px-3.5 py-1.5 rounded-xl font-bold text-xs sm:text-sm shadow-xs">
              <BookOpen className="w-4 h-4 text-emerald-200" />
              <span>Self-Paced (Untimed)</span>
            </div>
          ) : effectiveMode === 'adaptive' ? (
            <div className="flex items-center gap-2 bg-purple-700 text-white px-3.5 py-1.5 rounded-xl font-bold text-xs sm:text-sm shadow-xs">
              <BrainCircuit className="w-4 h-4 text-purple-200" />
              <span>Adaptive Calibration</span>
            </div>
          ) : effectiveMode === 'rapid' ? (
            <div
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl font-mono font-bold text-sm sm:text-base shadow-xs transition-colors ${
                rapidTimeLeft <= 10
                  ? 'bg-rose-600 text-white animate-pulse'
                  : 'bg-amber-500 text-slate-950'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>{rapidTimeLeft}s Remaining</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-slate-900 text-white px-3.5 py-1.5 rounded-xl font-mono font-bold text-sm sm:text-base shadow-xs">
              <Clock className="w-4 h-4 text-amber-400" />
              <span>{formatTime(timeLeftSeconds)}</span>
            </div>
          )}

          {!isSubmitted && (
            <button
              onClick={() => handleInitiateSubmit()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm px-4 py-2 rounded-xl transition shadow-xs"
            >
              Submit Exam
            </button>
          )}
        </div>
      </div>

      {/* Rapid Fire Progress Bar (Linear 45s countdown) */}
      {effectiveMode === 'rapid' && !isSubmitted && (
        <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden shadow-inner">
          <div
            className={`h-full transition-all duration-1000 ${
              rapidTimeLeft <= 10 ? 'bg-rose-500' : 'bg-amber-500'
            }`}
            style={{ width: `${Math.max(0, (rapidTimeLeft / 45) * 100)}%` }}
          />
        </div>
      )}

      {/* Main Exam Area or Score Report */}
      {!isSubmitted ? (
        activeQuestions.length === 0 && currentEssayPrompt ? (
          /* Pure Essay / Constructed Response Exam Mode (e.g. CBEST Writing) */
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-6">
              {/* Essay Prompt Selector */}
              {activeEssayPrompts.length > 1 && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-800 flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-indigo-600" />
                      Select Constructed Response Task (
                      {activeEssayPrompts.length} Prompts):
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono">
                      Task {selectedPromptIndex + 1} of{' '}
                      {activeEssayPrompts.length}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {activeEssayPrompts.map((ep, pIdx) => {
                      const isSelected = pIdx === selectedPromptIndex;
                      return (
                        <button
                          key={ep.id || pIdx}
                          type="button"
                          onClick={() => {
                            setSelectedPromptIndex(pIdx);
                            setEssayFeedback(null);
                          }}
                          className={`p-3.5 rounded-xl border text-left transition flex items-start gap-3 cursor-pointer ${
                            isSelected
                              ? 'bg-white border-indigo-600 ring-2 ring-indigo-500/20 shadow-xs'
                              : 'bg-white/70 border-slate-200 hover:bg-white hover:border-slate-300'
                          }`}
                        >
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0 mt-0.5 ${
                              isSelected
                                ? 'bg-indigo-600 text-white'
                                : 'bg-slate-200 text-slate-700'
                            }`}
                          >
                            {pIdx + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-bold text-slate-900 text-xs sm:text-sm truncate">
                                {ep.title}
                              </span>
                              <span
                                className={`px-2 py-0.5 text-[9px] font-extrabold uppercase rounded ${
                                  ep.type === 'argumentative'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {ep.type === 'argumentative'
                                  ? 'Analytical / Argumentative'
                                  : 'Expressive / Personal'}
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-500 font-mono block mt-1">
                              ⏱️ Suggested: {ep.timeLimitMinutes || 45} mins
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Active Prompt Header & Text */}
              <div className="space-y-3 border-b border-slate-100 pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    {currentEssayPrompt.title}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2.5 py-1 text-xs font-extrabold uppercase rounded-full ${
                        currentEssayPrompt.type === 'argumentative'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {currentEssayPrompt.type === 'argumentative'
                        ? 'Analytical / Argumentative'
                        : 'Expressive / Personal Experience'}
                    </span>
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-mono font-bold rounded-full">
                      ⏱️ {currentEssayPrompt.timeLimitMinutes || 45} min limit
                    </span>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-sm sm:text-base text-slate-800 leading-relaxed font-medium">
                    {currentEssayPrompt.promptText}
                  </p>
                </div>
              </div>

              {/* Essay Text Area */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Your Response
                  </label>
                  <span className="text-xs text-slate-500 font-mono">
                    {essayText.trim().split(/\s+/).filter(Boolean).length} Words
                  </span>
                </div>

                <textarea
                  rows={14}
                  placeholder="Type your essay response here for instant AI holistic scoring according to the CTC 1-4 holistic rubric..."
                  value={essayText}
                  onChange={(e) => setEssayText(e.target.value)}
                  className="w-full p-4 rounded-xl border border-slate-300 text-sm text-slate-900 leading-relaxed focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              {/* Evaluation Button */}
              <div className="flex items-center justify-between gap-4 pt-2">
                <button
                  onClick={handleEvaluateEssay}
                  disabled={evaluatingEssay || !essayText.trim()}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-sm px-5 py-3 rounded-xl transition shadow-xs flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4" />
                  {evaluatingEssay
                    ? 'Scoring Essay with AI...'
                    : 'Evaluate Essay with AI (Holistic Scoring)'}
                </button>
              </div>

              {/* AI Evaluation Feedback Card */}
              {essayFeedback && (
                <div className="p-5 rounded-xl bg-slate-900 text-slate-100 text-sm leading-relaxed space-y-3 animate-in fade-in duration-200">
                  <div className="font-bold text-amber-300 border-b border-slate-800 pb-2 flex items-center justify-between">
                    <span>AI Holistic Score & Diagnostic Feedback</span>
                    <span className="text-xs text-slate-400 font-normal">
                      CTC Holistic Writing Rubric
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap">{essayFeedback}</div>
                </div>
              )}
            </div>
          </div>
        ) : currentQuestion ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Question Box (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-6">
                {/* Question Header Badge */}
                <div className="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                      Domain: {currentQuestion.domain}
                    </span>
                    {effectiveMode === 'adaptive' && (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase bg-purple-100 text-purple-800 border border-purple-200">
                        Target Tier: {adaptiveDifficulty}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFlag(currentQuestion.id)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition ${
                        flaggedQuestions[currentQuestion.id]
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <Flag className="w-3.5 h-3.5" />
                      {flaggedQuestions[currentQuestion.id]
                        ? 'Flagged'
                        : 'Flag Question'}
                    </button>

                    <button
                      onClick={handleAskAiTutor}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-indigo-600" /> Ask
                      AI Tutor
                    </button>
                  </div>
                </div>

                {/* Question Text */}
                <p className="text-base sm:text-lg font-semibold text-slate-900 leading-relaxed">
                  {currentQuestion.questionText}
                </p>

                {/* Rapid Fire Flash Snapshot Banner */}
                {effectiveMode === 'rapid' && rapidFlashActive && (
                  <div className="p-4 rounded-2xl bg-amber-500 text-slate-950 shadow-lg border border-amber-400 space-y-2 animate-in fade-in zoom-in-95">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 font-black text-sm">
                        <Zap className="w-4 h-4 fill-slate-950" />
                        <span>Instant Snapshot Rationale:</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold bg-black/20 px-2 py-0.5 rounded-lg text-slate-950">
                          Next in {rapidFlashSeconds}s
                        </span>
                        <button
                          type="button"
                          onClick={advanceRapidQuestion}
                          className="px-2.5 py-1 rounded-lg bg-slate-950 hover:bg-black text-white text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                        >
                          <span>Next Now</span>
                          <FastForward className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm font-bold leading-snug">
                      {getOneSentenceRationale(currentQuestion)}
                    </p>
                  </div>
                )}

                {/* Options List */}
                <div className="space-y-3">
                  {currentQuestion.options.map((option, optIdx) => {
                    const isSelected =
                      userAnswers[currentQuestion.id] === optIdx;
                    const isAnswered =
                      userAnswers[currentQuestion.id] !== undefined;
                    const isStruck =
                      struckOptions[`${currentQuestion.id}-${optIdx}`];
                    const isCorrectAnswer =
                      optIdx === currentQuestion.correctAnswerIndex;

                    // Styling logic per mode
                    let optionStyles =
                      'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/50';

                    if (
                      (effectiveMode === 'study' ||
                        effectiveMode === 'adaptive') &&
                      isAnswered
                    ) {
                      if (isCorrectAnswer) {
                        optionStyles =
                          'bg-emerald-50 border-emerald-500 text-emerald-900 ring-2 ring-emerald-500/20 font-semibold shadow-xs';
                      } else if (isSelected && !isCorrectAnswer) {
                        optionStyles =
                          'bg-rose-50 border-rose-500 text-rose-900 ring-2 ring-rose-500/20 font-semibold shadow-xs';
                      } else {
                        optionStyles =
                          'bg-slate-50/60 border-slate-200 opacity-60';
                      }
                    } else if (effectiveMode === 'rapid' && isAnswered) {
                      if (isCorrectAnswer) {
                        optionStyles =
                          'bg-emerald-50 border-emerald-500 text-emerald-900 ring-2 ring-emerald-500/20 font-semibold shadow-xs';
                      } else if (isSelected && !isCorrectAnswer) {
                        optionStyles =
                          'bg-rose-50 border-rose-500 text-rose-900 ring-2 ring-rose-500/20 font-semibold shadow-xs';
                      } else {
                        optionStyles =
                          'bg-slate-50/60 border-slate-200 opacity-60';
                      }
                    } else {
                      if (isSelected) {
                        optionStyles =
                          'bg-indigo-50/90 border-indigo-500 ring-2 ring-indigo-500/20 shadow-xs';
                      }
                    }

                    const isDisabledInteraction =
                      (effectiveMode === 'timed' && isAnswered) ||
                      (effectiveMode === 'rapid' && isAnswered);

                    return (
                      <div
                        key={optIdx}
                        onClick={() => handleSelectOption(optIdx)}
                        className={`p-4 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                          isDisabledInteraction
                            ? 'cursor-default'
                            : 'cursor-pointer'
                        } ${isStruck ? 'opacity-40 line-through bg-slate-50' : ''} ${optionStyles}`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-6 h-6 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 ${
                              (effectiveMode === 'study' ||
                                effectiveMode === 'adaptive') &&
                              isAnswered
                                ? isCorrectAnswer
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : isSelected
                                    ? 'bg-rose-600 text-white border-rose-600'
                                    : 'border-slate-300 text-slate-500'
                                : isSelected
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'border-slate-300 text-slate-600'
                            }`}
                          >
                            {(effectiveMode === 'study' ||
                              effectiveMode === 'adaptive') &&
                            isAnswered &&
                            isCorrectAnswer ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : (effectiveMode === 'study' ||
                                effectiveMode === 'adaptive') &&
                              isAnswered &&
                              isSelected &&
                              !isCorrectAnswer ? (
                              <XCircle className="w-4 h-4" />
                            ) : (
                              String.fromCharCode(65 + optIdx)
                            )}
                          </div>
                          <span className="text-sm font-medium text-slate-800">
                            {option}
                          </span>
                        </div>

                        {effectiveMode !== 'study' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleStrikeOption(currentQuestion.id, optIdx);
                            }}
                            title="Strike-through distractor"
                            className="p-1 text-slate-400 hover:text-slate-600 rounded transition"
                          >
                            <Strikethrough className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Study / Adaptive Mode: Immediate Full Explanation & Rationale Card */}
                {(effectiveMode === 'study' || effectiveMode === 'adaptive') &&
                  userAnswers[currentQuestion.id] !== undefined && (
                    <div className="mt-4 p-5 rounded-2xl bg-emerald-50/80 border border-emerald-200 text-slate-800 space-y-2.5 animate-in fade-in duration-200">
                      <div className="flex items-center gap-2 text-emerald-900 font-extrabold text-sm">
                        <BookOpen className="w-4 h-4 text-emerald-700" />
                        <span>Pedagogical Rationale & Full Explanation:</span>
                      </div>
                      <div className="text-xs sm:text-sm text-slate-700 leading-relaxed font-medium">
                        {renderCleanExplanation(currentQuestion.explanation)}
                      </div>
                    </div>
                  )}

                {/* Action Buttons Footer */}
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-4">
                  {effectiveMode === 'timed' ? (
                    <button
                      disabled={true}
                      title="In Timed Exam Mode, answers lock immediately and you cannot go back to previous questions."
                      className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-400 cursor-not-allowed bg-slate-50 flex items-center gap-1.5"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      <span>Locked in Timed Exam</span>
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        setCurrentIndex((prev) => Math.max(0, prev - 1))
                      }
                      disabled={currentIndex === 0 || effectiveMode === 'rapid'}
                      className="px-4 py-2 rounded-xl border border-slate-300 text-xs font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-100 transition"
                    >
                      &larr; Previous Question
                    </button>
                  )}

                  {effectiveMode === 'adaptive' ? (
                    currentIndex < activeQuestions.length - 1 ||
                    hasMoreAdaptiveQuestions ? (
                      <button
                        onClick={handleAdvanceAdaptive}
                        className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold transition shadow-xs flex items-center gap-1.5 cursor-pointer"
                      >
                        <span>Next Question</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleInitiateSubmit()}
                        className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition shadow-xs flex items-center gap-1.5"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Submit Exam</span>
                      </button>
                    )
                  ) : currentIndex < activeQuestions.length - 1 ? (
                    <button
                      onClick={() => {
                        if (effectiveMode === 'rapid') {
                          advanceRapidQuestion();
                        } else {
                          setCurrentIndex((prev) =>
                            Math.min(activeQuestions.length - 1, prev + 1),
                          );
                        }
                      }}
                      className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition shadow-xs flex items-center gap-1.5"
                    >
                      <span>Next Question</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleInitiateSubmit()}
                      className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition shadow-xs flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Submit Exam</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Optional Essay Writing Task Section (if 5723 Writing paper or paper has essay prompts) */}
              {currentEssayPrompt && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
                  {/* If paper has more than one prompt, provide a selector */}
                  {activeEssayPrompts.length > 1 && (
                    <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-800 flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-indigo-600" />
                          Select Essay Prompt Task ({
                            activeEssayPrompts.length
                          }{' '}
                          available):
                        </span>
                        <span className="text-[11px] text-slate-500 font-mono">
                          Prompt {selectedPromptIndex + 1} of{' '}
                          {activeEssayPrompts.length}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {activeEssayPrompts.map((ep, pIdx) => {
                          const isSelected = pIdx === selectedPromptIndex;
                          return (
                            <button
                              key={ep.id || pIdx}
                              type="button"
                              onClick={() => {
                                setSelectedPromptIndex(pIdx);
                                setEssayFeedback(null);
                              }}
                              className={`p-3 rounded-xl border text-left transition flex items-start gap-2.5 cursor-pointer ${
                                isSelected
                                  ? 'bg-white border-indigo-600 ring-2 ring-indigo-500/20 shadow-xs'
                                  : 'bg-white/70 border-slate-200 hover:bg-white hover:border-slate-300'
                              }`}
                            >
                              <div
                                className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5 ${
                                  isSelected
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-slate-200 text-slate-700'
                                }`}
                              >
                                {pIdx + 1}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-bold text-slate-900 text-xs truncate max-w-[180px] sm:max-w-[220px]">
                                    {ep.title}
                                  </span>
                                  <span
                                    className={`px-1.5 py-0.2 text-[9px] font-extrabold uppercase rounded ${
                                      ep.type === 'argumentative'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}
                                  >
                                    {ep.type === 'argumentative'
                                      ? 'Argumentative'
                                      : 'Informative'}
                                  </span>
                                </div>
                                <span className="text-[10px] text-slate-500 font-mono block mt-0.5">
                                  ⏱️ {ep.timeLimitMinutes || 30} mins
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between border-b border-slate-100 pb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                        <FileText className="w-5 h-5 text-indigo-600" />
                        {currentEssayPrompt.title}
                      </h3>
                      <span
                        className={`px-2 py-0.5 text-[10px] font-extrabold uppercase rounded ${
                          currentEssayPrompt.type === 'argumentative'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {currentEssayPrompt.type === 'argumentative'
                          ? 'Argumentative Task'
                          : 'Informative / Explanatory Task'}
                      </span>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-mono font-bold rounded">
                        ⏱️ {currentEssayPrompt.timeLimitMinutes || 30} min limit
                      </span>
                    </div>

                    <span className="text-xs text-slate-500 font-mono">
                      {essayText.trim().split(/\s+/).filter(Boolean).length}{' '}
                      Words
                    </span>
                  </div>

                  <p className="text-xs text-slate-700 leading-relaxed bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                    {currentEssayPrompt.promptText}
                  </p>

                  <textarea
                    rows={8}
                    placeholder="Type your essay response here for instant AI holistic scoring (1-6 scale)..."
                    value={essayText}
                    onChange={(e) => setEssayText(e.target.value)}
                    className="w-full p-4 rounded-xl border border-slate-300 text-xs sm:text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />

                  <div className="flex items-center justify-between gap-4">
                    <button
                      onClick={handleEvaluateEssay}
                      disabled={evaluatingEssay || !essayText.trim()}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                    >
                      <Sparkles className="w-4 h-4" />
                      {evaluatingEssay
                        ? 'Scoring Essay with AI...'
                        : 'Evaluate Essay with AI'}
                    </button>
                  </div>

                  {essayFeedback && (
                    <div className="p-4 rounded-xl bg-slate-900 text-slate-100 text-xs leading-relaxed space-y-2">
                      <div className="font-bold text-amber-300 border-b border-slate-800 pb-1 flex items-center justify-between">
                        <span>AI Holistic Score & Feedback (1-6 Scale)</span>
                        <span className="text-[10px] text-slate-400 font-normal">
                          ETS Holistic Writing Rubric
                        </span>
                      </div>
                      <div className="whitespace-pre-wrap">{essayFeedback}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Question Navigator Drawer (1 col) */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4 self-start">
              <h4 className="font-bold text-slate-900 text-sm border-b border-slate-100 pb-2">
                Question Navigator{' '}
                {effectiveMode === 'adaptive'
                  ? `(${activeQuestions.filter((q) => userAnswers[q.id] !== undefined && userAnswers[q.id] !== null).length} Completed)`
                  : `(${activeQuestions.filter((q) => userAnswers[q.id] !== undefined && userAnswers[q.id] !== null).length}/${activeQuestions.length})`}
              </h4>

              <div className="grid grid-cols-5 gap-2">
                {activeQuestions.map((q, idx) => {
                  const isAnswered =
                    userAnswers[q.id] !== undefined &&
                    userAnswers[q.id] !== null;
                  const isFlagged = flaggedQuestions[q.id];
                  const isCurrent = idx === currentIndex;

                  return (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-9 rounded-lg text-xs font-bold transition flex items-center justify-center relative ${
                        isCurrent
                          ? 'bg-indigo-600 text-white ring-2 ring-indigo-600/30'
                          : isAnswered
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {idx + 1}
                      {isFlagged && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="pt-2 text-[11px] text-slate-500 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 inline-block" />{' '}
                  Answered
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-amber-500 inline-block" />{' '}
                  Flagged
                </div>
              </div>
            </div>
          </div>
        ) : null
      ) : (
        /* SCORE REPORT CERTIFICATE DISPLAY */
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 sm:p-10 space-y-8">
          <div className="text-center space-y-3 max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-indigo-100 text-indigo-600 mx-auto flex items-center justify-center shadow-inner">
              <Award className="w-8 h-8" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              PRAXIS {paper.code} Exam Score Report
            </h2>

            <div className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-600">
              <MapPin className="w-4 h-4 text-indigo-600" />
              Evaluation for State Standard:{' '}
              <span className="text-slate-900 font-bold">
                {activeState.name}
              </span>
            </div>
          </div>

          {/* Big Score Cards */}
          {(() => {
            const displayScore = testResult
              ? testResult.score
              : Math.min(
                  200,
                  Math.max(
                    100,
                    Math.round(
                      100 +
                        Math.round(
                          (Object.keys(userAnswers).filter((qId) => {
                            const q = activeQuestions.find(
                              (item) => item.id === qId,
                            );
                            return (
                              q && userAnswers[qId] === q.correctAnswerIndex
                            );
                          }).length /
                            (activeQuestions.length || 1)) *
                            100,
                        ) *
                          1.0,
                    ),
                  ),
                );

            const isQualified = testResult
              ? testResult.passed
              : displayScore >= passScoreTarget;

            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto text-center">
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-xs font-bold text-slate-500 uppercase">
                    Estimated ETS Score
                  </div>
                  <div className="text-4xl font-extrabold text-slate-900 mt-2">
                    {displayScore}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Scale 100 - 200
                  </div>
                </div>

                <div className="p-5 rounded-2xl bg-indigo-50 border border-indigo-200">
                  <div className="text-xs font-bold text-indigo-600 uppercase">
                    Passing Threshold
                  </div>
                  <div className="text-4xl font-extrabold text-indigo-900 mt-2">
                    {passScoreTarget}
                  </div>
                  <div className="text-xs text-indigo-600 mt-1">
                    {activeState.code} Required Score
                  </div>
                </div>

                <div
                  className={`p-5 rounded-2xl ${isQualified ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}
                >
                  <div
                    className={`text-xs font-bold uppercase ${isQualified ? 'text-emerald-600' : 'text-rose-600'}`}
                  >
                    Status Result
                  </div>
                  <div
                    className={`text-2xl font-extrabold mt-2 flex items-center justify-center gap-1 ${isQualified ? 'text-emerald-700' : 'text-rose-700'}`}
                  >
                    {isQualified ? (
                      <>
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />{' '}
                        QUALIFIED
                      </>
                    ) : (
                      <>
                        <XCircle className="w-6 h-6 text-rose-600" /> NOT
                        QUALIFIED
                      </>
                    )}
                  </div>
                  <div
                    className={`text-xs mt-1 ${isQualified ? 'text-emerald-600' : 'text-rose-600'}`}
                  >
                    {isQualified
                      ? 'Exceeds State Standard'
                      : 'Below Passing Threshold'}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Review Question Explanations */}
          <div className="space-y-4 pt-6 border-t border-slate-200">
            <h3 className="text-lg font-bold text-slate-900">
              Detailed Question Explanations & Key Insights
            </h3>

            <div className="space-y-4">
              {activeQuestions.map((q, idx) => {
                const userChoice = userAnswers[q.id];
                const isCorrect = userChoice === q.correctAnswerIndex;

                return (
                  <div
                    key={q.id}
                    className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2"
                  >
                    <div className="flex items-center justify-between text-xs font-bold">
                      <span className="text-slate-500">
                        Question {idx + 1} ({q.domain})
                      </span>
                      <span
                        className={
                          isCorrect ? 'text-emerald-600' : 'text-rose-600'
                        }
                      >
                        {isCorrect ? '✓ Correct' : '✕ Incorrect'}
                      </span>
                    </div>

                    <p className="text-sm font-semibold text-slate-900">
                      {q.questionText}
                    </p>

                    <p className="text-xs text-slate-700 leading-relaxed bg-white p-3 rounded-lg border border-slate-200">
                      <strong className="text-indigo-600">Explanation: </strong>{' '}
                      {q.explanation}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={onBackToPapers}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-xl transition shadow-md"
            >
              Return to Papers Directory
            </button>
          </div>
        </div>
      )}

      {/* AI Tutor Explanation Modal */}
      {showAiModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-4 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2 font-bold text-slate-900 text-base">
                <Sparkles className="w-5 h-5 text-indigo-600 animate-pulse" />
                AI Step-by-Step Question Guidance
              </div>
              <button
                onClick={() => setShowAiModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold p-1 rounded-lg transition"
              >
                ✕
              </button>
            </div>

            {loadingAiExplanation && !aiExplanation ? (
              <div className="py-8 text-center text-xs text-slate-500 space-y-2">
                <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="font-semibold text-slate-700 text-sm">
                  Consulting Gemini AI Exam Coach...
                </p>
              </div>
            ) : (
              <div className="space-y-3 text-xs sm:text-sm text-slate-800 max-h-[28rem] overflow-y-auto leading-relaxed p-4 rounded-xl bg-slate-50 border border-slate-200 font-normal">
                {renderCleanExplanation(aiExplanation)}
                {loadingAiExplanation && (
                  <span className="inline-block w-2 h-4 bg-indigo-600 animate-pulse ml-1 align-middle" />
                )}
              </div>
            )}

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setShowAiModal(false)}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl text-xs transition shadow-xs"
              >
                Got it, close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unanswered Questions Confirmation Modal */}
      {showUnansweredModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-amber-100 text-amber-800 shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-900">
                  Unanswered Questions Warning
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  You have{' '}
                  <span className="font-bold text-slate-900">
                    {unansweredIndices.length}
                  </span>{' '}
                  unanswered question(s):{' '}
                  <span className="font-semibold text-amber-700">
                    {unansweredIndices.map((i) => `#${i + 1}`).join(', ')}
                  </span>
                  .
                </p>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mt-1">
                  Unanswered questions will be marked incorrect. Submit anyway,
                  or go back and answer them?
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-end gap-3">
              <button
                onClick={handleReviewUnanswered}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs sm:text-sm transition shadow-xs flex items-center justify-center gap-1.5"
              >
                <span>Review Unanswered Questions</span>
              </button>
              <button
                onClick={handleConfirmSubmitAnyway}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-bold text-xs sm:text-sm transition flex items-center justify-center"
              >
                <span>Submit Anyway</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
